import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import api from "@/lib/api";
import PublicLayout from "@/components/PublicLayout";
import ArticleCard from "@/components/ArticleCard";
import NewsletterSignup from "@/components/NewsletterSignup";

function formatDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }); }
  catch { return ""; }
}

function readingTime(text) {
  const words = (text || "").trim().split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.round(words / 220));
}

function slugifyHeading(text) {
  return String(text).toLowerCase().replace(/[^\w\s-]/g, "").trim().replace(/\s+/g, "-").slice(0, 80);
}

/** Extract H2 (and optional H3) headings from markdown for TOC */
function extractToc(md) {
  const lines = (md || "").split("\n");
  const items = [];
  for (const line of lines) {
    const m2 = line.match(/^##\s+(.+?)\s*$/);
    const m3 = line.match(/^###\s+(.+?)\s*$/);
    if (m2) {
      const text = m2[1].replace(/[*_`]/g, "");
      items.push({ level: 2, text, id: slugifyHeading(text) });
    } else if (m3) {
      const text = m3[1].replace(/[*_`]/g, "");
      items.push({ level: 3, text, id: slugifyHeading(text) });
    }
  }
  return items;
}

function SocialShare({ title, url }) {
  const [copied, setCopied] = useState(false);
  const t = encodeURIComponent(title);
  const u = encodeURIComponent(url);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {}
  };

  const linkClass =
    "w-10 h-10 rounded-full border border-[var(--rciq-line)] flex items-center justify-center text-[var(--rciq-ink-2)] hover:bg-[var(--rciq-teal)] hover:text-white hover:border-[var(--rciq-teal)] transition";

  return (
    <div className="flex items-center gap-2" data-testid="social-share">
      <span className="text-[11px] uppercase tracking-[0.14em] text-[var(--rciq-muted)] font-700 mr-2">Share</span>
      <a
        href={`https://www.linkedin.com/sharing/share-offsite/?url=${u}`}
        target="_blank" rel="noopener noreferrer"
        className={linkClass}
        data-testid="share-linkedin" aria-label="Share on LinkedIn"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14M18.5 18.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.32 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.79M6.88 8.56a1.68 1.68 0 0 0 1.68-1.68c0-.93-.75-1.69-1.68-1.69a1.69 1.69 0 0 0-1.69 1.69c0 .93.76 1.68 1.69 1.68m1.39 9.94v-8.37H5.5v8.37h2.77z"/></svg>
      </a>
      <a
        href={`https://twitter.com/intent/tweet?text=${t}&url=${u}`}
        target="_blank" rel="noopener noreferrer"
        className={linkClass}
        data-testid="share-twitter" aria-label="Share on X"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
      </a>
      <a
        href={`mailto:?subject=${t}&body=${u}`}
        className={linkClass}
        data-testid="share-email" aria-label="Share by email"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="5" width="18" height="14" rx="2"/><path d="m3 7 9 6 9-6"/></svg>
      </a>
      <button onClick={copy} className={linkClass} data-testid="share-copy" aria-label="Copy link">
        {copied ? (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
        ) : (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
        )}
      </button>
    </div>
  );
}

export default function ArticleDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    api.get(`/articles/${slug}`)
      .then(({ data }) => {
        setArticle(data);
        if (data.seo_description) {
          let meta = document.querySelector('meta[name="description"]');
          if (!meta) { meta = document.createElement('meta'); meta.name = 'description'; document.head.appendChild(meta); }
          meta.content = data.seo_description;
        }
        document.title = `${data.title} — Revenue Cycle IQ Insights`;
        return api.get("/articles", { params: { category: data.category, limit: 4 } });
      })
      .then((res) => {
        if (!res) return;
        const items = (res.data.items || []).filter((a) => a.slug !== slug).slice(0, 3);
        setRelated(items);
      })
      .catch((e) => { if (e.response?.status === 404) setNotFound(true); })
      .finally(() => setLoading(false));
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [slug]);

  const toc = useMemo(() => extractToc(article?.content), [article]);

  if (loading) {
    return (
      <PublicLayout>
        <div className="max-w-3xl mx-auto px-6 py-32 text-center text-[var(--rciq-muted)]" data-testid="article-loading">
          Loading…
        </div>
      </PublicLayout>
    );
  }

  if (notFound || !article) {
    return (
      <PublicLayout>
        <div className="max-w-3xl mx-auto px-6 py-32 text-center" data-testid="article-not-found">
          <h1 className="font-serif-display text-4xl font-700">Insight not found</h1>
          <p className="mt-4 text-[var(--rciq-muted)]">It may have been moved or unpublished.</p>
          <button onClick={() => navigate("/")} className="mt-8 btn-pill btn-pill-primary text-[12px]">
            Back to Insights
          </button>
        </div>
      </PublicLayout>
    );
  }

  const currentUrl = typeof window !== "undefined" ? window.location.href : "";

  // JSON-LD Article schema
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.seo_description || article.excerpt,
    image: article.cover_image ? [article.cover_image] : [],
    datePublished: article.published_at,
    dateModified: article.updated_at || article.published_at,
    author: { "@type": "Organization", name: article.author_name || "Revenue Cycle IQ" },
    publisher: {
      "@type": "Organization",
      name: "Revenue Cycle IQ",
      url: "https://www.therevenuecycleiq.com",
    },
    keywords: (article.tags || []).join(", "),
    mainEntityOfPage: { "@type": "WebPage", "@id": currentUrl },
  };

  // Custom heading components that inject ids for TOC anchors
  const mdComponents = {
    h2: ({ children, ...props }) => {
      const text = Array.isArray(children) ? children.join("") : String(children || "");
      return <h2 id={slugifyHeading(text)} {...props}>{children}</h2>;
    },
    h3: ({ children, ...props }) => {
      const text = Array.isArray(children) ? children.join("") : String(children || "");
      return <h3 id={slugifyHeading(text)} {...props}>{children}</h3>;
    },
  };

  return (
    <PublicLayout>
      {/* eslint-disable-next-line react/no-danger */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />

      <article data-testid="article-detail">
        {/* Hero */}
        <header className="bg-[var(--rciq-navy)] text-white pt-16 pb-20 md:pt-20 md:pb-28 relative overflow-hidden">
          <div className="absolute inset-0 bg-dot-pattern opacity-50 pointer-events-none" />
          <div className="relative max-w-4xl mx-auto px-6 lg:px-10">
            <Link to="/" className="text-[11px] uppercase tracking-[0.18em] text-[var(--rciq-teal)] font-700 inline-flex items-center gap-2 hover:gap-3 transition-all" data-testid="back-to-insights">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" /></svg>
              All Insights
            </Link>
            <div className="mt-6 flex items-center gap-3 flex-wrap">
              <span className="tag-pill tag-pill-dark">{article.category}</span>
              <span className="text-xs text-white/60">{formatDate(article.published_at)}</span>
              <span className="text-xs text-white/60">·</span>
              <span className="text-xs text-white/60">{article.reading_time || readingTime(article.content)} min read</span>
            </div>
            <h1 className="font-serif-display text-4xl md:text-6xl leading-[1.05] font-700 mt-6 tracking-tight">
              {article.title}
            </h1>
            {article.excerpt && (
              <p className="mt-6 text-lg md:text-xl text-white/80 leading-relaxed max-w-3xl">
                {article.excerpt}
              </p>
            )}
            <div className="mt-8 flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center overflow-hidden flex-shrink-0">
                <img
                  src="https://customer-assets.emergentagent.com/job_insights-hub-72/artifacts/go7xudtz_ChatGPT%20Image%20May%2028%2C%202026%2C%2001_32_16%20PM.png"
                  alt="Revenue Cycle IQ"
                  className="h-10 w-10 object-contain"
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                />
              </div>
              <div>
                <div className="text-sm font-700">{article.author_name}</div>
                {article.author_title && <div className="text-xs text-white/60">{article.author_title}</div>}
              </div>
            </div>
          </div>
        </header>

        {/* Cover */}
        {article.cover_image && (
          <div className="max-w-5xl mx-auto px-6 lg:px-10 -mt-12 md:-mt-16 relative z-10">
            <img
              src={article.cover_image}
              alt={article.title}
              className="w-full rounded-2xl shadow-2xl aspect-[16/9] object-cover"
              data-testid="article-cover-image"
            />
          </div>
        )}

        {/* Body — TOC sidebar + content */}
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-16 md:py-20">
          <div className="grid lg:grid-cols-[240px_1fr] gap-12">
            {/* TOC */}
            <aside className="hidden lg:block" data-testid="toc-sidebar">
              <div className="sticky top-28">
                {toc.length > 0 && (
                  <>
                    <div className="eyebrow mb-4">On this page</div>
                    <nav className="space-y-2 border-l border-[var(--rciq-line)] pl-4" data-testid="toc-nav">
                      {toc.map((item, i) => (
                        <a
                          key={`${item.id}-${i}`}
                          href={`#${item.id}`}
                          data-testid={`toc-link-${item.id}`}
                          className={`block text-sm leading-snug transition-colors hover:text-[var(--rciq-teal-dark)] ${
                            item.level === 3
                              ? "pl-3 text-[var(--rciq-muted)]"
                              : "text-[var(--rciq-ink-2)] font-600"
                          }`}
                        >
                          {item.text}
                        </a>
                      ))}
                    </nav>
                  </>
                )}
                <div className="mt-8">
                  <SocialShare title={article.title} url={currentUrl} />
                </div>
              </div>
            </aside>

            {/* Content */}
            <div className="max-w-3xl">
              {/* Mobile share */}
              <div className="lg:hidden mb-6">
                <SocialShare title={article.title} url={currentUrl} />
              </div>

              {/* Key takeaways */}
              {Array.isArray(article.key_takeaways) && article.key_takeaways.length > 0 && (
                <div
                  data-testid="key-takeaways"
                  className="mb-12 rounded-2xl border-l-4 border-[var(--rciq-teal)] bg-[var(--rciq-bg-tint)] p-6 md:p-8"
                >
                  <div className="eyebrow mb-3">Key Takeaways</div>
                  <ul className="space-y-3">
                    {article.key_takeaways.map((kt, i) => (
                      <li key={i} className="flex gap-3 text-[var(--rciq-ink)] leading-relaxed">
                        <svg className="flex-shrink-0 mt-1 text-[var(--rciq-teal-dark)]" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                        <span>{kt}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="prose-rciq" data-testid="article-content">
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
                  {article.content}
                </ReactMarkdown>
              </div>

              {Array.isArray(article.tags) && article.tags.length > 0 && (
                <div className="mt-12 pt-8 border-t border-[var(--rciq-line)]">
                  <div className="eyebrow mb-3">Tagged</div>
                  <div className="flex flex-wrap gap-2">
                    {article.tags.map((t) => (
                      <Link key={t} to={`/?tag=${encodeURIComponent(t)}`} className="tag-pill hover:bg-[var(--rciq-teal)] hover:text-white transition" data-testid={`article-tag-${t}`}>
                        {t}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Newsletter inline */}
        <div className="max-w-4xl mx-auto px-6 lg:px-10 pb-16">
          <NewsletterSignup variant="light" />
        </div>

        {/* Related */}
        {related.length > 0 && (
          <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-20" data-testid="related-section">
            <div className="eyebrow mb-6">Related Insights</div>
            <h2 className="font-serif-display text-3xl md:text-4xl font-700 mb-10 tracking-tight">More in {article.category}</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {related.map((a, i) => <ArticleCard key={a.id} article={a} index={i} />)}
            </div>
          </section>
        )}

        {/* Footer CTA */}
        <section className="bg-[var(--rciq-bg-tint)] border-t border-[var(--rciq-line)]" data-testid="article-footer-cta">
          <div className="max-w-5xl mx-auto px-6 lg:px-10 py-20 md:py-24 text-center">
            <h2 className="font-serif-display text-3xl md:text-5xl leading-[1.1] font-700 tracking-tight">
              Ready to Improve Revenue Cycle Performance?
            </h2>
            <p className="mt-5 text-base md:text-lg text-[var(--rciq-ink-2)] max-w-3xl mx-auto leading-relaxed">
              Explore Revenue Cycle IQ's consulting services, analytics solutions, and executive
              intelligence tools designed to help healthcare organizations improve financial outcomes.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://www.therevenuecycleiq.com/book-a-call" target="_blank" rel="noopener noreferrer"
                className="btn-pill btn-pill-primary text-[12px]"
                data-testid="article-cta-schedule"
              >
                Schedule a Consultation
              </a>
              <a
                href="https://www.therevenuecycleiq.com/services" target="_blank" rel="noopener noreferrer"
                className="btn-pill btn-pill-ghost text-[12px]"
                data-testid="article-cta-services"
              >
                Explore Solutions
              </a>
            </div>
          </div>
        </section>
      </article>
    </PublicLayout>
  );
}
